import { Box, Typography } from '@mui/material';
import { BettingType, StakeErrors } from '../../../../@types';
import { useBetslipContext } from '../../../../contexts/BetslipContext';
import type { NeobetBetslipDrawerController } from '../../../../hooks/components/useNeobetBetslipDrawer';
import useLocalization from '../../../../hooks/useLocalization';
import { getLocalizedBettingType } from '../../../../helpers';
import { useEffect } from 'react';
import NeobetBetslipStakeInput from './NeobetBetslipStakeInput';
import NeobetBetslipStakeMessage from './NeobetBetslipStakeMessage';

type NeobetBetslipSystemSectionProps = {
  showSystemBetInputs: NeobetBetslipDrawerController['showSystemBetInputs'];
  combinations: NeobetBetslipDrawerController['combinations'];
  lastCombinationKey: NeobetBetslipDrawerController['lastCombinationKey'];
  stakeAmounts: NeobetBetslipDrawerController['stakeAmounts'];
  stakeErrors: StakeErrors;
  updateStakeAmount: NeobetBetslipDrawerController['updateStakeAmount'];
  validateStake: NeobetBetslipDrawerController['validateStake'];
  handleStakeChange: NeobetBetslipDrawerController['handleStakeChange'];
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  card: {
    minWidth: 0,
    pl: 1,
    pr: 0,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'neutral.600',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 92px',
    alignItems: 'center',
    gap: 1,
  },
  cardInfo: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0.75,
  },
  cardLabel: {
    minWidth: 0,
    color: 'text.primary',
    typography: 'body3',
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardCount: {
    flexShrink: 0,
    color: 'neutral.300',
    typography: 'body3',
    fontWeight: 700,
  },
} as const;

type NeobetSystemInputRowProps = {
  bettingType: BettingType;
  combinationValue: number;
  stakeValue: string;
  betslipTicketType: ReturnType<typeof useBetslipContext>['betslipTicketType'];
  selectedWallet: ReturnType<typeof useBetslipContext>['selectedWallet'];
  stakeErrors: StakeErrors;
  updateStakeAmount: NeobetBetslipDrawerController['updateStakeAmount'];
  validateStake: NeobetBetslipDrawerController['validateStake'];
  handleStakeChange: NeobetBetslipDrawerController['handleStakeChange'];
};

const NeobetSystemInputRow = ({
  bettingType,
  combinationValue,
  stakeValue,
  betslipTicketType,
  selectedWallet,
  stakeErrors,
  updateStakeAmount,
  validateStake,
  handleStakeChange,
}: NeobetSystemInputRowProps) => {
  const { t } = useLocalization();
  const localizedBettingType = getLocalizedBettingType(t, bettingType);
  const stakeErrorType = stakeErrors[bettingType]?.errorType;

  useEffect(() => {
    validateStake(bettingType, stakeValue, false, betslipTicketType, combinationValue);
  }, [betslipTicketType, combinationValue, stakeValue, bettingType, validateStake]);

  return (
    <Box sx={styles.card} data-testid={`neobet-system-input-row-${bettingType}`}>
      <Box sx={styles.cardInfo}>
        <Typography component='span' sx={styles.cardLabel} data-testid={`neobet-system-input-label-${bettingType}`}>
          {localizedBettingType}
        </Typography>
        <Typography component='span' sx={styles.cardCount} data-testid={`neobet-system-input-count-${bettingType}`}>
          {combinationValue}x
        </Typography>
      </Box>
      <NeobetBetslipStakeInput
        value={stakeValue}
        helperMessage={
          stakeErrorType ? (
            <NeobetBetslipStakeMessage
              errorType={stakeErrorType}
              ticketType={betslipTicketType}
              selectedWallet={selectedWallet}
              testId={`neobet-system-input-stake-message-${bettingType}`}
            />
          ) : undefined
        }
        onChange={(value) =>
          handleStakeChange(bettingType, value, updateStakeAmount, false, betslipTicketType, combinationValue)
        }
        width='100%'
        testId={`neobet-system-input-stake-${bettingType}`}
      />
    </Box>
  );
};

const NeobetBetslipSystemSection = ({
  showSystemBetInputs,
  combinations,
  lastCombinationKey,
  stakeAmounts,
  stakeErrors,
  updateStakeAmount,
  validateStake,
  handleStakeChange,
}: NeobetBetslipSystemSectionProps) => {
  const { bets, betslipTicketType, selectedWallet } = useBetslipContext();

  if (bets.length <= 1 || !showSystemBetInputs) {
    return null;
  }

  return (
    <Box sx={styles.root} data-testid='neobet-betslip-system-section'>
      {Object.entries(combinations).map(([combinationKey, combinationValue]) => {
        if (!lastCombinationKey || combinationKey === lastCombinationKey) {
          return null;
        }

        const bettingType = combinationKey as BettingType;

        return (
          <NeobetSystemInputRow
            key={combinationKey}
            bettingType={bettingType}
            combinationValue={combinationValue}
            stakeValue={stakeAmounts[bettingType] ?? ''}
            betslipTicketType={betslipTicketType}
            selectedWallet={selectedWallet}
            stakeErrors={stakeErrors}
            updateStakeAmount={updateStakeAmount}
            validateStake={validateStake}
            handleStakeChange={handleStakeChange}
          />
        );
      })}
    </Box>
  );
};

export default NeobetBetslipSystemSection;
